import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { ConfirmHelperService, SpinnerService } from '@zayan-erp/shared';
import { finalize, from, of, switchMap, throwError } from 'rxjs';
import { DealPresenceService, LockAttempt } from '../services/deal-presence.service';

/**
 * Requests that must go through even while the open deal is locked. Everything else sent from a
 * deal page is treated as an edit to that deal - most deal writes carry a child id rather than
 * the deal id, so the URL cannot tell us which deal they belong to.
 */
const ALWAYS_ALLOWED = ['/token/', '/authentication', '/login', '/notification', '/loginactivity'];

/** 423 Locked - the status a server-side lock check would answer with. */
const LOCKED_STATUS = 423;

/**
 * Thrown instead of sending the request, so callers see a failure rather than a silent no-op.
 * Shaped like an API error response, because the API services read the message from `error.error`.
 */
export class DealLockedError extends HttpErrorResponse {
  constructor(holder: string, url: string) {
    const message = holder ? `${holder} is editing this deal.` : 'This deal is being edited by someone else.';

    super({
      error: { success: false, message, data: null },
      status: LOCKED_STATUS,
      statusText: 'Locked',
      url
    });
  }
}

export const dealLockInterceptor: HttpInterceptorFn = (req, next) => {
  const presence = inject(DealPresenceService);
  const confirm = inject(ConfirmHelperService);
  const spinner = inject(SpinnerService);

  const editing = !!presence.dealId && req.method !== 'GET' && !ALWAYS_ALLOWED.some(path => req.url.includes(path));

  if (!editing || presence.heldByMe()) return next(req);

  const blocked = () => throwError(() => new DealLockedError(presence.holderName(), req.urlWithParams));

  /**
   * The offer to take over lives here rather than on the chip, because this is the moment the user
   * has shown they want to edit. Taking over succeeds, so the write they just attempted goes on.
   *
   * API callers start their loader before this interceptor runs. Hide it while the prompt is open
   * so the overlay does not block the dialog, then resume only if the write continues.
   */
  const refuse = () => {
    const holder = presence.holderName();
    spinner.pauseForUserPrompt();

    return presence
      .confirmTakeOver(() =>
        confirm.showModal({
          icon: 'confirm-icon',
          header: 'Deal locked',
          message: holder
            ? `${holder} is editing this deal right now. You can wait for them to finish, or take control - they will be told and will lose their lock.`
            : 'Someone else is editing this deal right now. You can wait for them to finish, or take control - they will be told and will lose their lock.',
          acceptLabel: 'Take control',
          acceptSeverity: 'danger',
          rejectLabel: 'Cancel'
        })
      )
      .pipe(
        switchMap(takeOver => (takeOver ? from(presence.takeOverLock()) : of<LockAttempt>('refused'))),
        // Only the server saying no keeps the write back; a lock we could not reach is not a refusal.
        switchMap(taken => {
          if (taken === 'refused') return blocked();
          spinner.resumeAfterUserPrompt();
          return next(req);
        }),
        finalize(() => spinner.clearPromptPause())
      );
  };

  // Known to be someone else's - refuse without a round trip.
  if (presence.lockedByOther()) return refuse();

  // Believed free. The server is the authority, so claim it before the write goes out.
  return from(presence.tryAcquireLock()).pipe(switchMap(result => (result === 'refused' ? refuse() : next(req))));
};
